import React, { useState } from 'react';
import { X, Plus, Trash2, Check, ChevronRight, Target, Shield, Activity, Brain } from 'lucide-react';
import { db } from '../../config/firebase';
import { FIRESTORE_PATHS } from '../../config/appConfig';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { useToast } from '../../contexts/ToastContext';
import logger from '../../utils/logger';

const DrillCreator = ({ userId, onComplete, onCancel }) => {
    const [formData, setFormData] = useState({
        name: '',
        role_focus: 'Batting',
        category: 'Technical Foundation',
        setup_instructions: '',
        execution_steps: [''],
        recommended_volume: '',
        min_players: 1,
        space_req: 'Net',
        tags: []
    });

    const [loading, setLoading] = useState(false);
    const { error: showError } = useToast();

    const categories = {
        'Batting': ['Technical Foundation', 'Against Pace', 'Against Spin', 'Against Bounce'],
        'Bowling': ['Action & Rhythm', 'Accuracy', 'Variations'],
        'Fielding': ['Catching', 'Ground Fielding', 'Reaction'],
        'Fitness': ['Speed & Stamina', 'Strength & Power', 'Recovery'],
        'Mental': ['Visualization', 'VR Training', 'Focus']
    };

    const handleStepChange = (index, value) => {
        const newSteps = [...formData.execution_steps];
        newSteps[index] = value;
        setFormData({ ...formData, execution_steps: newSteps });
    };

    const addStep = () => {
        setFormData({ ...formData, execution_steps: [...formData.execution_steps, ''] });
    };

    const removeStep = (index) => {
        if (formData.execution_steps.length > 1) {
            const newSteps = formData.execution_steps.filter((_, i) => i !== index);
            setFormData({ ...formData, execution_steps: newSteps });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name) return;

        setLoading(true);
        try {
            const drillData = {
                ...formData,
                drill_id: `custom_${Date.now()}`,
                isUserCreated: true,
                createdAt: serverTimestamp(),
                tags: [...formData.tags, formData.min_players > 1 ? 'Partner' : 'Solo', formData.space_req]
            };

            const customDrillsRef = collection(db, FIRESTORE_PATHS.USERS, userId, FIRESTORE_PATHS.CUSTOM_DRILLS);
            await addDoc(customDrillsRef, drillData);

            onComplete(drillData);
        } catch (error) {
            logger.error('DrillCreator', 'Error saving custom drill:', error);
            showError("Failed to save drill. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/95 z-[100] flex flex-col animate-fadeIn">
            <div className="p-4 border-b border-gray-800 flex justify-between items-center bg-gray-900 sticky top-0 z-10">
                <div className="flex items-center gap-3">
                    <div className="bg-purple-600/20 p-2 rounded-lg text-purple-400">
                        <Plus size={20} />
                    </div>
                    <h2 className="text-xl font-bold text-white">Custom Drill</h2>
                </div>
                <button onClick={onCancel} className="p-2 bg-gray-800 rounded-full text-gray-400 hover:text-white transition-colors">
                    <X size={20} />
                </button>
            </div>

            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-8 pb-32">

                {/* Basic Info */}
                <div className="space-y-4">
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Drill Name</label>
                        <input
                            required
                            type="text"
                            placeholder="e.g., Backfoot Punch Master"
                            className="w-full bg-gray-800 border border-gray-700 rounded-xl p-4 text-white focus:border-purple-500 outline-none transition-all text-lg font-bold"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Role Focus</label>
                            <select
                                className="w-full bg-gray-800 border border-gray-700 rounded-xl p-3 text-white outline-none"
                                value={formData.role_focus}
                                onChange={(e) => setFormData({ ...formData, role_focus: e.target.value, category: categories[e.target.value][0] })}
                            >
                                {Object.keys(categories).map(role => <option key={role} value={role}>{role}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Category</label>
                            <select
                                className="w-full bg-gray-800 border border-gray-700 rounded-xl p-3 text-white outline-none"
                                value={formData.category}
                                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                            >
                                {categories[formData.role_focus].map(cat => <option key={cat} value={cat}>{cat}</option>)}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Requirements */}
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Space Req</label>
                        <select
                            className="w-full bg-gray-800 border border-gray-700 rounded-xl p-3 text-white outline-none text-sm"
                            value={formData.space_req}
                            onChange={(e) => setFormData({ ...formData, space_req: e.target.value })}
                        >
                            <option value="Net">Cricket Net</option>
                            <option value="Home">Home / Small Space</option>
                            <option value="Outfield">Outfield / Park</option>
                            <option value="Gym">Gym</option>
                            <option value="Wall">Against Wall</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Players</label>
                        <select
                            className="w-full bg-gray-800 border border-gray-700 rounded-xl p-3 text-white outline-none text-sm"
                            value={formData.min_players}
                            onChange={(e) => setFormData({ ...formData, min_players: parseInt(e.target.value) })}
                        >
                            <option value={1}>Solo (1 Player)</option>
                            <option value={2}>Partner (2 Players)</option>
                            <option value={3}>Small Group (3+)</option>
                        </select>
                    </div>
                </div>

                {/* Instructions */}
                <div className="space-y-6">
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Setup (What do you need?)</label>
                        <textarea
                            placeholder="e.g., 2 Cones, a partner, and 6 balls..."
                            className="w-full bg-gray-800 border border-gray-700 rounded-xl p-4 text-white resize-none h-24 text-sm"
                            value={formData.setup_instructions}
                            onChange={(e) => setFormData({ ...formData, setup_instructions: e.target.value })}
                        />
                    </div>

                    <div className="space-y-3">
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Execution Steps</label>
                        {formData.execution_steps.map((step, idx) => (
                            <div key={idx} className="flex gap-2">
                                <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-3 flex-1 flex items-start gap-3">
                                    <span className="text-purple-500 font-bold text-xs mt-1">{idx + 1}</span>
                                    <input
                                        type="text"
                                        placeholder="Add a step..."
                                        className="bg-transparent border-none outline-none text-white w-full text-sm"
                                        value={step}
                                        onChange={(e) => handleStepChange(idx, e.target.value)}
                                    />
                                </div>
                                {formData.execution_steps.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => removeStep(idx)}
                                        className="p-3 bg-red-900/20 text-red-400 rounded-xl hover:bg-red-900/40 transition-colors"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                )}
                            </div>
                        ))}
                        <button
                            type="button"
                            onClick={addStep}
                            className="w-full py-3 rounded-xl border border-dashed border-gray-700 text-gray-500 hover:text-gray-300 transition-all flex items-center justify-center gap-2 text-xs font-bold uppercase"
                        >
                            <Plus size={14} /> Add Next Step
                        </button>
                    </div>
                </div>

                {/* Target Volume */}
                <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Recommended Volume</label>
                    <input
                        type="text"
                        placeholder="e.g., 3 Sets / 15 Reps or 10 Minutes"
                        className="w-full bg-gray-800 border border-gray-700 rounded-xl p-4 text-white text-sm"
                        value={formData.recommended_volume}
                        onChange={(e) => setFormData({ ...formData, recommended_volume: e.target.value })}
                    />
                </div>

            </form>

            <div className="p-4 bg-gray-900 border-t border-gray-800 sticky bottom-0 z-10">
                <button
                    disabled={loading || !formData.name}
                    onClick={handleSubmit}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-4 rounded-xl shadow-lg shadow-purple-900/20 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                    {loading ? "CREATING..." : "SAVE & ADD TO LIBRARY"}
                </button>
            </div>
        </div>
    );
};

export default DrillCreator;
